/**
 * PayoffOptions — read-only "Explore payoff options".
 *
 * Every row is a scenario the backend computed. Nothing here saves anything:
 * the input only re-runs the projection.
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Lock } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { longMonth, horizon } from '@/lib/debtPlanFormat';

function saved(scn, label) {
  if (!scn || scn.never_clears) return 'Still never clears on these numbers.';
  const bits = [];
  if (scn.months_saved) bits.push(`${scn.months_saved} month${scn.months_saved === 1 ? '' : 's'} sooner`);
  const interest = Object.entries(scn.interest_saved_by_currency || {});
  if (interest.length) {
    bits.push(`saves ${interest.map(([c, v]) => formatMoney(v, c, { compact: true })).join(' + ')} in interest`);
  }
  return bits.length ? bits.join(', ') : (label || 'No change on these numbers.');
}

function Row({ testid, title, note, scn }) {
  if (!scn) return null;
  return (
    <li className="py-3 flex flex-wrap items-baseline justify-between gap-3" data-testid={testid}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-charcoal">{title}</p>
        {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
      </div>
      <div className="text-right">
        <p className="text-sm text-charcoal tabular-nums">
          {scn.payoff_date ? longMonth(scn.payoff_date) : '—'}
          {scn.months ? <span className="text-muted-foreground"> · {horizon(scn.months)}</span> : null}
        </p>
        <p className="text-xs text-muted-foreground">{saved(scn)}</p>
      </div>
    </li>
  );
}

export function PayoffOptions({ plan, extra, onExtraChange, busy }) {
  const s = plan?.scenarios || {};
  if (!s.current || !plan?.payoff_path?.projectable) return null;
  const ccy = s.strategies?.currency
    || plan.priorities?.best_for_extra?.currency
    || plan.scope?.display_currency;
  const strat = s.strategies;

  return (
    <div className="bg-gold/10 rounded-2xl border border-gold/30 p-5 shadow-sm mb-4"
         data-testid="payoff-options-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-1">
            Explore payoff options
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Lock className="h-3 w-3" aria-hidden="true" />
            Nothing here is saved — it only changes what you see.
          </p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1" htmlFor="payoff-extra-input">
            Pay extra each month {ccy ? `(${ccy})` : ''}
          </label>
          <Input
            id="payoff-extra-input"
            value={extra}
            inputMode="decimal"
            onChange={(e) => onExtraChange(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="5000"
            className="h-9 w-32 bg-card"
            data-testid="payoff-extra-input"
          />
        </div>
      </div>

      <ul className={`mt-3 divide-y divide-gold/25 ${busy ? 'opacity-60' : ''}`}>
        <Row
          testid="payoff-option-current"
          title="What you're doing now"
          note="Each debt keeps paying what it pays today."
          scn={s.current}
        />
        <Row
          testid="payoff-option-roll"
          title="Roll each cleared payment into the next debt"
          note="Same total outgoing, applied where it works hardest."
          scn={s.roll_freed_payments}
        />
        <Row
          testid="payoff-option-annual"
          title="One extra payment a year"
          note="A 13th EMI, from a bonus or a good month."
          scn={s.one_extra_payment_a_year}
        />
        {s.extra_monthly && (
          <Row
            testid="payoff-option-extra"
            title={`${formatMoney(s.extra_monthly.extra_monthly, s.extra_monthly.currency)} more every month`}
            note={s.extra_monthly.applied_to
              ? `Applied to ${s.extra_monthly.applied_to.name}${s.extra_monthly.applied_to.apr ? ` (${s.extra_monthly.applied_to.apr}%)` : ''}, then rolled onward.`
              : null}
            scn={s.extra_monthly}
          />
        )}
      </ul>

      {strat && Number(extra) > 0 && strat.avalanche?.months && strat.snowball?.months && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="payoff-strategy-compare">
          With {formatMoney(strat.extra_monthly, strat.currency)} a month spare, highest-rate-first
          clears everything in {horizon(strat.avalanche.months)} and smallest-balance-first in{' '}
          {horizon(strat.snowball.months)}.
          {strat.avalanche.months === strat.snowball.months
            ? ' Same either way here — pick whichever keeps you going.'
            : ''}
        </p>
      )}

      {(plan.payoff_path.assumptions || []).includes('interest_free_where_apr_missing') && (
        <p className="text-[11px] text-muted-foreground mt-2">
          A debt with no rate recorded is projected interest-free, so the real dates are later.
        </p>
      )}
    </div>
  );
}

export default PayoffOptions;
